import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';

export function useCrud({ urls = null, token = null, contentType = null } = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const activeRequests = useRef(0);
  const abortControllers = useRef({});
  

  const updateLoadingState = (change) => {
    activeRequests.current += change;
    setLoading(activeRequests.current > 0);
  };

  const abortPreviousRequest = (scope) => {
    if (scope !== 'default' && abortControllers.current[scope]) {
      abortControllers.current[scope].abort();
      console.log(`Requisição anterior cancelada no escopo: ${scope}`);
    }
    abortControllers.current[scope] = new AbortController();
  };

  const fetchWithToken = async (url, method, data = null, customContentType = null, signal, is_auth = true, timeout = 0) => {
    updateLoadingState(1);
  
    const hasFormat = /[?&]format=json/.test(url);
    if (!hasFormat) {
      const hasQuery = url.includes('?');
      url += hasQuery ? '&format=json' : '?format=json';
    }
  
    const config = {
      method,
      url,
      headers: {
        //...(is_auth && token ? { Authorization: `token ${token}` } : {}),
        'Content-Type': customContentType || contentType || 'application/json',
      },
      data,
      signal,
      timeout, // 👈 adiciona aqui
      is_auth,
    };
  
    try {
      const response = await api(config);
      return response.data;
    } catch (err) {
      if (err.name !== 'CanceledError') {
        if (err.response?.status === 403) {
          Swal.fire({
            title: `Erro ${err.response.status}`,
            text: err.response.data.detail || 'Acesso negado',
            icon: 'error',
          });
        } else if (err.response?.status === 404) {
          Swal.fire({
            title: `Erro ${err.response.status}`,
            text: err.response?.data?.detail || 'Endereço não encontrado na API',
            icon: 'error',
          });
        } else if (err.code === 'ECONNABORTED') { // timeout
          // Swal.fire({
          //   title: 'Tempo excedido',
          //   text: 'A requisição demorou demais para responder.',
          //   icon: 'warning',
          // });
        }
      }
      throw err;
    } finally {
      updateLoadingState(-1);
    }
  };

  const performRequest = async (method, data = null, customUrl = null, customContentType = null, scope = 'default', is_auth = true, timeout = 0) => {
    setError(null);
    
    if (scope !== 'default') {
      abortPreviousRequest(scope);
    }
  
    const signal = abortControllers.current[scope]?.signal;
  
    try {
      urls = urls ? Array.isArray(urls) ? urls : [urls] : null;
      const requestUrls = customUrl ? (Array.isArray(customUrl) ? customUrl : [customUrl]) : urls;
      const promises = requestUrls.map((url) =>
        fetchWithToken(url, method, data, customContentType, signal, is_auth, timeout)
      );
  
      return await Promise.all(promises);
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  useEffect(() => {
    return () => {
      Object.keys(abortControllers.current).forEach((scope) => {
        if (scope !== 'default') {
          abortPreviousRequest(scope);
        }
      });
    };
  }, []);
  
  const baseURL = api.defaults.baseURL;

  const get = (customUrl = null, scope = 'default', is_auth = true, timeout = 0) => performRequest('GET', null, customUrl, null, scope, is_auth, timeout);
  const post = (data, customUrl = null, customContentType = null, scope = 'default', is_auth = true, timeout = 0) => performRequest('POST', data, customUrl, customContentType, scope, is_auth, timeout);
  const put = (data, customUrl = null, customContentType = null, scope = 'default', is_auth = true, timeout = 0) => performRequest('PUT', data, customUrl, customContentType, scope, is_auth, timeout);
  const patch = (data, customUrl = null, customContentType = null, scope = 'default', is_auth = true, timeout = 0) => performRequest('PATCH', data, customUrl, customContentType, scope, is_auth, timeout);
  const del = (customUrl = null, scope = 'default', is_auth = true, timeout = 0) => performRequest('DELETE', null, customUrl, null, scope, is_auth, timeout);

  return { 
    error, 
    loading, 
    get, 
    post, 
    put, 
    patch, 
    del,
    baseURL
  };
}

export default useCrud;